import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { Job, Variation } from '@cartwhl/common/types';
import { getStudioUrl } from '@cartwhl/common/utils';
import DaysAgo from '@cartwhl/ui/atoms/DaysAgo';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import Button from '@cartwhl/ui/input/Button';
import EmptyState from '@cartwhl/ui/messaging/EmptyState';

import { galleryStore, noticesStore, userStore } from 'stores/global';

import GalleryList from './GalleryList';
import ProfileSection from './ProfileSection';
import ProfileTabLoadingState from './ProfileTabLoadingState';

const FETCH_LIMIT = 1000;

type SortType = 'All' | 'Lists';
type SortBy = 'Newest' | 'Oldest';

export default function Favorites() {
  const { loading: userLoading } = useSnapshot(userStore.state);
  const { favoritesRecords } = useSnapshot(galleryStore.state);

  const [sorted, setSorted] = useState<Job[]>([]);
  const [filter, setFilter] = useState<SortType>('All');
  const [sortBy, setSortBy] = useState<SortBy>('Newest');

  const records = useMemo(
    () => (favoritesRecords.data?.items || []) as Job[],
    [favoritesRecords.data?.items],
  );

  useEffect(() => {
    if (records.length > 0) {
      const list = Array.prototype.slice.call(records, 0);
      if (sortBy === 'Newest') {
        setSorted(list);
      } else {
        setSorted(list.reverse());
      }
    }
  }, [records, sortBy]);

  useEffect(() => {
    if (records.length === 0 && favoritesRecords.status === 'initial') {
      galleryStore.fetchFavorites(FETCH_LIMIT, false);
    }
  }, [records.length, favoritesRecords.status]);

  if (userLoading) {
    return <ProfileTabLoadingState />;
  }

  return (
    <div className="overview max-w-(--breakpoint-md) mx-auto">
      <ProfileSection
        title="Favorites"
        boxed={false}
        className="mt-8"
        actions={
          <div className="flex flex-nowrap gap-2">
            <Button
              variant={filter === 'All' ? 'secondary' : 'tertiary'}
              onClick={() => setFilter('All')}
            >
              All
            </Button>
            <Button
              variant={filter === 'Lists' ? 'secondary' : 'tertiary'}
              onClick={() => setFilter('Lists')}
            >
              Lists
            </Button>
            <Dropdown trigger={<Button variant="tertiary" icon={<SortIcon />} />}>
              <Text
                as="div"
                variant="web/body/md"
                className="px-4 py-2 opacity-30 dark:opacity-50"
              >
                Sort by
              </Text>
              <Dropdown.Item onClick={() => setSortBy('Newest')}>
                <div>
                  <CheckmarkIcon
                    className={`mr-2 inline ${
                      sortBy === 'Newest' ? 'opacity-100' : 'opacity-0'
                    }`}
                  />{' '}
                  Newest
                </div>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy('Oldest')}>
                <div>
                  <CheckmarkIcon
                    className={`mr-2 inline ${
                      sortBy === 'Oldest' ? 'opacity-100' : 'opacity-0'
                    }`}
                  />{' '}
                  Oldest
                </div>
              </Dropdown.Item>
            </Dropdown>
          </div>
        }
      >
        {filter === 'Lists' ? (
          <EmptyState description="Coming soon" />
        ) : (
          <FavoritesList sorted={sorted} />
        )}
        <div className="container my-10 flex justify-center">
          <InfiniteLoadingTrigger
            loading={favoritesRecords.status === 'pending'}
            onEnter={() => galleryStore.fetchFavorites(FETCH_LIMIT, false)}
            done={
              (favoritesRecords.status === 'ready' &&
                favoritesRecords.data?.nextToken === undefined) ||
              favoritesRecords.status === 'error'
            }
          />
        </div>
      </ProfileSection>
    </div>
  );
}

function FavoritesList({ sorted }: { sorted: Job[] }) {
  const { favoritesRecords } = useSnapshot(galleryStore.state);

  const done =
    (favoritesRecords.status === 'ready' &&
      favoritesRecords.data?.nextToken === undefined) ||
    favoritesRecords.status === 'error';

  if (done && !galleryStore.hasFavorites()) {
    return <EmptyState description="You have no favorites" />;
  }

  // Create a custom asyncState with sorted items, preserving the exact type structure
  const sortedAsyncState =
    favoritesRecords.status === 'ready'
      ? {
          status: 'ready' as const,
          data: {
            items: sorted,
            nextToken: favoritesRecords.data?.nextToken,
          },
        }
      : favoritesRecords.status === 'pending'
        ? {
            status: 'pending' as const,
            data: favoritesRecords.data
              ? {
                  items:
                    sorted.length > 0 ? sorted : favoritesRecords.data.items,
                  nextToken: favoritesRecords.data.nextToken,
                }
              : undefined,
          }
        : favoritesRecords;

  return (
    <GalleryList
      asyncState={sortedAsyncState}
      variant="grid"
      maxPlaceholders={FETCH_LIMIT}
      onCardRender={(job: Job, variation: Variation) => (
        <GalleryFoot job={job} variation={variation} />
      )}
    />
  );
}

function GalleryFoot({ job, variation }: { job: Job; variation: Variation }) {
  const timeout = useRef<null | NodeJS.Timeout>(null);
  const [isActive, setIsActive] = useState<boolean>(false);
  const styles: { boxShadow?: string; display?: string } = {
    boxShadow: '-1rem 0 1rem white',
  };

  if (isActive) {
    styles.display = 'block';
  }

  return (
    <div>
      <p>
        <Text variant="web/body/md" className="opacity-50 dark:opacity-70">
          <DaysAgo date={job.createdAt} />
        </Text>
      </p>
      <div
        className="dark:bg-gray-90 z-1000 absolute right-0 top-0 hidden bg-white group-hover:block"
        style={styles}
      >
        <Dropdown
          trigger={<Button variant="tertiary" icon={<ThreeDotsIcon />} />}
          onOpenChange={(open) => {
            if (timeout.current) {
              clearTimeout(timeout.current);
            }
            if (open) {
              setIsActive(true);
            } else {
              timeout.current = setTimeout(setIsActive, 300, false);
            }
          }}
        >
          <Dropdown.Item
            onClick={() => {
              window.location.href = `${getStudioUrl()}/home?jobID=${
                job.jobID
              }&animationNumber=${variation.variationID}`;
            }}
          >
            Open
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              navigator.clipboard.writeText(
                `${getStudioUrl()}/home?jobID=${job.jobID}&animationNumber=${
                  variation.variationID
                }`,
              );
              noticesStore.info('URL Copied to clipboard!');
            }}
          >
            Copy share link
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              galleryStore.setFavorite(job.jobID, variation.variationID, false);
            }}
          >
            <span className="text-red">Remove favorite</span>
          </Dropdown.Item>
        </Dropdown>
      </div>
    </div>
  );
}
